import React, { useState } from "react";
import { useAuth } from "../../api/hooks/useAuth";
import { useToast } from "../../admin/context/ToastContext";

const ResetPasswordModal = ({ isOpen, onClose, email, onResetSuccess }) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { resetPassword, loading } = useAuth();
  const { showToast } = useToast();

  if (!isOpen) return null;

  const passwordRequirements = {
    hasUpper: /[A-Z]/.test(newPassword),
    hasLower: /[a-z]/.test(newPassword),
    hasNumber: /[0-9]/.test(newPassword),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
    isLong: newPassword.length >= 8,
  };

  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword) {
      showToast("Please enter a new password", "error");
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast("Passwords do not match", "error");
      return;
    }
    if (!isPasswordValid) {
      showToast("Please meet all password requirements", "error");
      return;
    }

    try {
      const result = await resetPassword(email, newPassword);
      if (result.status || result.success) {
        showToast(result.message || "Password reset successfully!", "success");
        onResetSuccess();
      } else {
        showToast(result.message || "Failed to reset password", "error");
      }
    } catch (err) {
      showToast(err.message || "An error occurred. Please try again.", "error");
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(15, 23, 42, 0.4)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2002,
        padding: "20px",
        animation: "fadeIn 0.3s ease-out",
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#FFFFFF",
          width: "100%",
          maxWidth: "450px",
          borderRadius: "24px",
          padding: "40px",
          boxShadow: "0 20px 50px rgba(15, 23, 42, 0.15)",
          position: "relative",
          animation: "slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            background: "#F3F4F6",
            border: "none",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: "#64748B",
          }}
        >
          ✕
        </button>

        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#0F172A", marginBottom: "8px" }}>
            Create New Password
          </h2>
          <p style={{ color: "#64748B", fontSize: "14px" }}>
            Set a secure password for <span style={{ fontWeight: "600", color: "#0F172A" }}>{email}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ fontSize: "14px", fontWeight: "600", color: "#0F172A" }}>New Password</label>
            <div style={{ position: "relative" }}>
              <input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  borderRadius: "12px",
                  border: "2px solid #E2E8F0",
                  fontSize: "16px",
                  outline: "none",
                  transition: "all 0.2s",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#A855F7")}
                onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  color: "#94A3B8",
                  cursor: "pointer",
                }}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ fontSize: "14px", fontWeight: "600", color: "#0F172A" }}>Confirm Password</label>
            <div style={{ position: "relative" }}>
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  borderRadius: "12px",
                  border: "2px solid #E2E8F0",
                  fontSize: "16px",
                  outline: "none",
                  transition: "all 0.2s",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#A855F7")}
                onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  color: "#94A3B8",
                  cursor: "pointer",
                }}
              >
                {showConfirmPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          {/* Password Requirements UI */}
          <div style={{ 
            backgroundColor: "#F8FAFC", 
            padding: "16px", 
            borderRadius: "12px", 
            display: "grid", 
            gridTemplateColumns: "1fr 1fr", 
            gap: "10px" 
          }}>
            {[
              { label: "8+ characters", met: passwordRequirements.isLong },
              { label: "Uppercase", met: passwordRequirements.hasUpper },
              { label: "Lowercase", met: passwordRequirements.hasLower },
              { label: "Number", met: passwordRequirements.hasNumber },
              { label: "Special char", met: passwordRequirements.hasSpecial },
            ].map((req, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px" }}>
                <div style={{ 
                  width: "16px", 
                  height: "16px", 
                  borderRadius: "50%", 
                  backgroundColor: req.met ? "#10B981" : "#E2E8F0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#FFFFFF",
                  fontSize: "10px"
                }}>
                  {req.met ? "✓" : ""}
                </div>
                <span style={{ color: req.met ? "#0F172A" : "#64748B" }}>{req.label}</span>
              </div>
            ))}
          </div>

          <button
            type="submit"
            disabled={loading || !isPasswordValid}
            style={{
              width: "100%",
              padding: "16px",
              borderRadius: "12px",
              border: "none",
              background: "linear-gradient(135deg, #A855F7 0%, #9333EA 100%)",
              color: "#FFFFFF",
              fontWeight: "600",
              fontSize: "16px",
              cursor: loading || !isPasswordValid ? "not-allowed" : "pointer",
              transition: "all 0.3s",
              opacity: loading || !isPasswordValid ? 0.7 : 1,
              marginTop: "8px",
              boxShadow: isPasswordValid ? "0 10px 15px -3px rgba(168, 85, 247, 0.4)" : "none",
            }}
          >
            {loading ? "Resetting..." : "Reset Password"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordModal;


